import type { ChestState } from './focus-challenge-model'

export type ServiceLevelState =
  | { status: 'success'; inSubscription: boolean }
  | { status: 'error' }
  | { status: 'pending' }

export type PlusClaimAction = 'claim' | 'paywall' | 'refetch' | 'error-dialog'

export function decidePlusClaim(input: {
  requiresSubscription: boolean
  serviceLevel: ServiceLevelState
  hasRefetched: boolean
}): PlusClaimAction {
  if (!input.requiresSubscription) return 'claim'
  if (input.serviceLevel.status === 'success') {
    return input.serviceLevel.inSubscription ? 'claim' : 'paywall'
  }
  // Subscription state is unresolved (error or still pending). Retry once; if
  // we have already retried, surface the error rather than guess.
  return input.hasRefetched ? 'error-dialog' : 'refetch'
}

export type PlusTapSurface = 'body' | 'chest'

export type PlusTapAction = PlusClaimAction | 'ignore'

// Decide what a tap on a Plus challenge card does, for either tap surface. The
// whole card is a paywall entry for non-subscribers: tapping the body or the
// chest in any chest state routes them to the paywall. Subscribers (and any
// non-Plus card) clear the subscription gate, after which only a ready chest
// claims — the body and a non-ready chest are inert. The deeplink arrow is not
// modelled here; it keeps its own behavior and stops propagation.
export function decidePlusTap(input: {
  surface: PlusTapSurface
  chestState: ChestState
  requiresSubscription: boolean
  serviceLevel: ServiceLevelState
  hasRefetched: boolean
}): PlusTapAction {
  const gate = decidePlusClaim({
    requiresSubscription: input.requiresSubscription,
    serviceLevel: input.serviceLevel,
    hasRefetched: input.hasRefetched,
  })
  // 'paywall' / 'refetch' / 'error-dialog' all pass straight through.
  if (gate !== 'claim') return gate
  return input.surface === 'chest' && input.chestState === 'ready'
    ? 'claim'
    : 'ignore'
}
